import {
    createAsyncThunk,
    createSlice,
} from "@reduxjs/toolkit";

import API from "../../../utils/apiCalling";
import { config } from "../../../utils/apiUrl";

/*
|--------------------------------------------------------------------------
| API INSTANCE
|--------------------------------------------------------------------------
*/

const api = new API();

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const getResponseBody = (
    response
) => {
    return (
        response?.data ||
        response ||
        {}
    );
};

const getResponseData = (
    response
) => {
    const body =
        getResponseBody(
            response
        );

    return (
        body?.data ??
        body
    );
};

const getErrorMessage = (
    error,
    fallbackMessage
) => {
    return (
        error?.response?.data
            ?.message ||
        error?.response?.data
            ?.error ||
        error?.payload?.message ||
        error?.message ||
        (
            typeof error ===
                "string"
                ? error
                : ""
        ) ||
        fallbackMessage
    );
};

const getCreditAmount = (
    creditNote
) => {
    const amount =
        Number(
            creditNote
                ?.totals
                ?.creditAmount ??
            creditNote
                ?.creditAmount ??
            creditNote
                ?.totalAmount ??
            0
        );

    return Number.isFinite(
        amount
    )
        ? amount
        : 0;
};

const updateRecordById = (
    records,
    updatedRecord
) => {
    if (
        !Array.isArray(
            records
        )
    ) {
        return [];
    }

    if (
        !updatedRecord?._id
    ) {
        return records;
    }

    return records.map(
        (
            record
        ) =>
            String(
                record?._id ||
                ""
            ) ===
            String(
                updatedRecord._id
            )
                ? updatedRecord
                : record
    );
};

const addRecordIfMissing = (
    records,
    newRecord
) => {
    const normalizedRecords =
        Array.isArray(
            records
        )
            ? records
            : [];

    if (
        !newRecord?._id
    ) {
        return normalizedRecords;
    }

    const alreadyExists =
        normalizedRecords.some(
            (
                record
            ) =>
                String(
                    record?._id ||
                        ""
                ) ===
                String(
                    newRecord._id
                )
        );

    if (
        alreadyExists
    ) {
        return updateRecordById(
            normalizedRecords,
            newRecord
        );
    }

    return [
        newRecord,
        ...normalizedRecords,
    ];
};

const buildSummaryFromRecords = (
    records = []
) => {
    const normalizedRecords =
        Array.isArray(
            records
        )
            ? records
            : [];

    return normalizedRecords.reduce(
        (
            summary,
            creditNote
        ) => {
            const status =
                String(
                    creditNote
                        ?.status ||
                        "DRAFT"
                ).toUpperCase();

            summary.total +=
                1;

            if (
                status ===
                "DRAFT"
            ) {
                summary.draft +=
                    1;
            }

            if (
                status ===
                "CONFIRMED"
            ) {
                summary.confirmed +=
                    1;

                summary.confirmedCreditAmount +=
                    getCreditAmount(
                        creditNote
                    );
            }

            if (
                status ===
                "CANCELLED"
            ) {
                summary.cancelled +=
                    1;
            }

            return summary;
        },
        {
            total: 0,
            draft: 0,
            confirmed: 0,
            cancelled: 0,
            confirmedCreditAmount: 0,
        }
    );
};

/*
|--------------------------------------------------------------------------
| CREATE CREDIT NOTE
|--------------------------------------------------------------------------
|
| POST /v1/admin/billing/credit-notes
|--------------------------------------------------------------------------
*/

export const createCreditNote =
    createAsyncThunk(
        "creditNote/createCreditNote",

        async (
            payload,
            {
                rejectWithValue,
            }
        ) => {
            try {
                const response =
                    await api.post(
                        config.createCreditNote(),
                        payload
                    );

                return getResponseData(
                    response
                );
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to create credit note."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| GET ALL CREDIT NOTES
|--------------------------------------------------------------------------
|
| GET /v1/admin/billing/credit-notes
|--------------------------------------------------------------------------
*/

export const fetchCreditNotes =
    createAsyncThunk(
        "creditNote/fetchCreditNotes",

        async (
            params = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                const query =
                    new URLSearchParams();

                Object.entries(
                    params ||
                        {}
                ).forEach(
                    ([
                        key,
                        value,
                    ]) => {
                        if (
                            value !==
                                undefined &&
                            value !==
                                null &&
                            value !==
                                ""
                        ) {
                            query.append(
                                key,
                                String(
                                    value
                                )
                            );
                        }
                    }
                );

                const baseUrl =
                    config.getCreditNotes();

                const url =
                    query.toString()
                        ? `${baseUrl}?${query.toString()}`
                        : baseUrl;

                const response =
                    await api.get(
                        url
                    );

                const body =
                    getResponseBody(
                        response
                    );

                /*
                |--------------------------------------------------------------------------
                | Supported response formats
                |--------------------------------------------------------------------------
                |
                | { data: [...] }
                |
                | { data: { records: [], pagination: {}, summary: {} } }
                |
                | { records: [], pagination: {}, summary: {} }
                |
                |--------------------------------------------------------------------------
                */

                const responseData =
                    body?.data ??
                    body ??
                    {};

                const records =
                    Array.isArray(
                        responseData
                    )
                        ? responseData
                        : Array.isArray(
                            responseData
                                ?.records
                        )
                            ? responseData.records
                            : Array.isArray(
                                responseData
                                    ?.creditNotes
                            )
                                ? responseData.creditNotes
                                : [];

                const pagination =
                    responseData
                        ?.pagination ||
                    body?.pagination ||
                    body?.meta ||
                    {};

                const summary =
                    responseData
                        ?.summary ||
                    body?.summary ||
                    {};

                return {
                    records,

                    summary,

                    pagination: {
                        page:
                            Number(
                                pagination
                                    ?.page ||
                                params
                                    ?.page ||
                                1
                            ),

                        limit:
                            Number(
                                pagination
                                    ?.limit ||
                                params
                                    ?.limit ||
                                20
                            ),

                        total:
                            Number(
                                pagination
                                    ?.total ??
                                records.length
                            ),

                        totalPages:
                            Number(
                                pagination
                                    ?.totalPages ||
                                1
                            ),

                        hasNextPage:
                            Boolean(
                                pagination
                                    ?.hasNextPage
                            ),

                        hasPreviousPage:
                            Boolean(
                                pagination
                                    ?.hasPreviousPage
                            ),
                    },
                };
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to fetch credit notes."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| GET CREDIT NOTE DETAILS
|--------------------------------------------------------------------------
|
| GET /v1/admin/billing/credit-notes/:creditNoteId
|--------------------------------------------------------------------------
*/

export const fetchCreditNoteById =
    createAsyncThunk(
        "creditNote/fetchCreditNoteById",

        async (
            creditNoteId,
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !creditNoteId
                ) {
                    return rejectWithValue(
                        "Credit note ID is required."
                    );
                }

                const response =
                    await api.get(
                        config.getCreditNoteById(
                            creditNoteId
                        )
                    );

                return getResponseData(
                    response
                );
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to fetch credit note."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| GET CREDIT NOTES BY SALE INVOICE
|--------------------------------------------------------------------------
|
| GET /v1/admin/billing/credit-notes/invoice/:invoiceId
|--------------------------------------------------------------------------
*/

export const fetchCreditNotesByInvoice =
    createAsyncThunk(
        "creditNote/fetchCreditNotesByInvoice",

        async (
            {
                invoiceId,
                includeCancelled = false,
            } = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !invoiceId
                ) {
                    return rejectWithValue(
                        "Sale invoice ID is required."
                    );
                }

                const baseUrl =
                    config.getCreditNotesByInvoice(
                        invoiceId
                    );

                const query =
                    new URLSearchParams();

                query.append(
                    "includeCancelled",
                    String(
                        includeCancelled
                    )
                );

                const response =
                    await api.get(
                        `${baseUrl}?${query.toString()}`
                    );

                const body =
                    getResponseBody(
                        response
                    );

                const responseData =
                    body?.data ||
                    body ||
                    {};

                return {
                    records:
                        Array.isArray(
                            responseData
                                ?.records
                        )
                            ? responseData.records
                            : Array.isArray(
                                responseData
                            )
                                ? responseData
                                : [],

                    summary: {
                        total:
                            Number(
                                responseData
                                    ?.summary
                                    ?.total ||
                                0
                            ),

                        confirmed:
                            Number(
                                responseData
                                    ?.summary
                                    ?.confirmed ||
                                0
                            ),

                        draft:
                            Number(
                                responseData
                                    ?.summary
                                    ?.draft ||
                                0
                            ),

                        cancelled:
                            Number(
                                responseData
                                    ?.summary
                                    ?.cancelled ||
                                0
                            ),

                        confirmedCreditAmount:
                            Number(
                                responseData
                                    ?.summary
                                    ?.confirmedCreditAmount ||
                                0
                            ),
                    },
                };
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to fetch invoice credit notes."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| CONFIRM CREDIT NOTE
|--------------------------------------------------------------------------
|
| PATCH /v1/admin/billing/credit-notes/:creditNoteId/confirm
|--------------------------------------------------------------------------
*/

export const confirmCreditNote =
    createAsyncThunk(
        "creditNote/confirmCreditNote",

        async (
            creditNoteId,
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !creditNoteId
                ) {
                    return rejectWithValue(
                        "Credit note ID is required."
                    );
                }

                const response =
                    await api.patch(
                        config.confirmCreditNote(
                            creditNoteId
                        ),
                        {}
                    );

                return getResponseData(
                    response
                );
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to confirm credit note."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| CANCEL CREDIT NOTE
|--------------------------------------------------------------------------
|
| PATCH /v1/admin/billing/credit-notes/:creditNoteId/cancel
|--------------------------------------------------------------------------
*/

export const cancelCreditNote =
    createAsyncThunk(
        "creditNote/cancelCreditNote",

        async (
            {
                creditNoteId,
                cancellationReason,
            } = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !creditNoteId
                ) {
                    return rejectWithValue(
                        "Credit note ID is required."
                    );
                }

                const normalizedReason =
                    String(
                        cancellationReason ||
                            ""
                    ).trim();

                if (
                    normalizedReason.length <
                    3
                ) {
                    return rejectWithValue(
                        "Cancellation reason must contain at least 3 characters."
                    );
                }

                const response =
                    await api.patch(
                        config.cancelCreditNote(
                            creditNoteId
                        ),
                        {
                            cancellationReason:
                                normalizedReason,
                        }
                    );

                return getResponseData(
                    response
                );
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to cancel credit note."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| APPLY CREDIT NOTE
|--------------------------------------------------------------------------
*/

export const applyCreditNote =
    createAsyncThunk(
        "creditNote/applyCreditNote",

        async (
            {
                creditNoteId,
                payload,
            } = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !creditNoteId
                ) {
                    return rejectWithValue(
                        "Credit note ID is required."
                    );
                }

                const response =
                    await api.post(
                        config.applyCreditNote(
                            creditNoteId
                        ),
                        payload
                    );

                return getResponseData(
                    response
                );
            } catch (
                error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to apply credit note."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| DEFAULT STATE
|--------------------------------------------------------------------------
*/

const defaultInvoiceSummary = {
    total: 0,
    confirmed: 0,
    draft: 0,
    cancelled: 0,
    confirmedCreditAmount: 0,
};

const defaultListSummary = {
    total: 0,
    draft: 0,
    confirmed: 0,
    cancelled: 0,
    confirmedCreditAmount: 0,
};

const defaultPagination = {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false,
};

const defaultLoading = {
    list: false,
    details: false,
    invoice: false,
    create: false,
    confirm: false,
    cancel: false,
    apply: false,
};

const defaultError = {
    list: "",
    details: "",
    invoice: "",
    create: "",
    confirm: "",
    cancel: "",
    apply: "",
};

/*
|--------------------------------------------------------------------------
| INITIAL STATE
|--------------------------------------------------------------------------
*/

const initialState = {
    /*
    |--------------------------------------------------------------------------
    | STANDALONE LIST
    |--------------------------------------------------------------------------
    */

    records: [],

    list: [],

    listSummary: {
        ...defaultListSummary,
    },

    pagination: {
        ...defaultPagination,
    },

    listPagination: {
        ...defaultPagination,
    },

    /*
    |--------------------------------------------------------------------------
    | SELECTED CREDIT NOTE
    |--------------------------------------------------------------------------
    */

    selectedCreditNote:
        null,

    /*
    |--------------------------------------------------------------------------
    | INVOICE CREDIT NOTES
    |--------------------------------------------------------------------------
    */

    invoiceCreditNotes: [],

    invoiceSummary: {
        ...defaultInvoiceSummary,
    },

    /*
    |--------------------------------------------------------------------------
    | REQUEST STATE
    |--------------------------------------------------------------------------
    */

    loading: {
        ...defaultLoading,
    },

    error: {
        ...defaultError,
    },

    successMessage:
        "",

    /*
    |--------------------------------------------------------------------------
    | APPLY RESULT
    |--------------------------------------------------------------------------
    */

    appliedResult:
        null,
};

/*
|--------------------------------------------------------------------------
| SLICE
|--------------------------------------------------------------------------
*/

const creditNoteSlice =
    createSlice({
        name:
            "creditNote",

        initialState,

        reducers: {
            clearCreditNoteError: (
                state
            ) => {
                state.error = {
                    ...defaultError,
                };
            },

            clearCreditNoteSuccess: (
                state
            ) => {
                state.successMessage =
                    "";
            },

            clearSelectedCreditNote: (
                state
            ) => {
                state.selectedCreditNote =
                    null;

                state.error.details =
                    "";
            },

            clearInvoiceCreditNotes: (
                state
            ) => {
                state.invoiceCreditNotes =
                    [];

                state.invoiceSummary = {
                    ...defaultInvoiceSummary,
                };

                state.error.invoice =
                    "";
            },

            clearCreditNoteList: (
                state
            ) => {
                state.records =
                    [];

                state.list =
                    [];

                state.listSummary = {
                    ...defaultListSummary,
                };

                state.pagination = {
                    ...defaultPagination,
                };

                state.listPagination = {
                    ...defaultPagination,
                };

                state.error.list =
                    "";
            },

            clearAppliedCreditNote: (
                state
            ) => {
                state.appliedResult =
                    null;

                state.error.apply =
                    "";
            },

            resetCreditNoteState: (
                state
            ) => {
                state.records =
                    [];

                state.list =
                    [];

                state.listSummary = {
                    ...defaultListSummary,
                };

                state.selectedCreditNote =
                    null;

                state.invoiceCreditNotes =
                    [];

                state.invoiceSummary = {
                    ...defaultInvoiceSummary,
                };

                state.pagination = {
                    ...defaultPagination,
                };

                state.listPagination = {
                    ...defaultPagination,
                };

                state.loading = {
                    ...defaultLoading,
                };

                state.error = {
                    ...defaultError,
                };

                state.successMessage =
                    "";

                state.appliedResult =
                    null;
            },
        },

        extraReducers: (
            builder
        ) => {
            /*
            |--------------------------------------------------------------------------
            | CREATE CREDIT NOTE
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    createCreditNote.pending,
                    (
                        state
                    ) => {
                        state.loading.create =
                            true;

                        state.error.create =
                            "";

                        state.successMessage =
                            "";
                    }
                )

                .addCase(
                    createCreditNote.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.create =
                            false;

                        const createdCreditNote =
                            action.payload;

                        state.selectedCreditNote =
                            createdCreditNote;

                        state.records =
                            addRecordIfMissing(
                                state.records,
                                createdCreditNote
                            );

                        state.list =
                            addRecordIfMissing(
                                state.list,
                                createdCreditNote
                            );

                        state.invoiceCreditNotes =
                            addRecordIfMissing(
                                state.invoiceCreditNotes,
                                createdCreditNote
                            );

                        state.listSummary =
                            buildSummaryFromRecords(
                                state.list
                            );

                        state.invoiceSummary = {
                            ...state.invoiceSummary,

                            ...buildSummaryFromRecords(
                                state.invoiceCreditNotes
                            ),
                        };

                        state.successMessage =
                            "Credit note created successfully.";
                    }
                )

                .addCase(
                    createCreditNote.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.create =
                            false;

                        state.error.create =
                            action.payload ||
                            "Unable to create credit note.";
                    }
                );

            /*
            |--------------------------------------------------------------------------
            | CREDIT NOTE LIST
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchCreditNotes.pending,
                    (
                        state
                    ) => {
                        state.loading.list =
                            true;

                        state.error.list =
                            "";
                    }
                )

                .addCase(
                    fetchCreditNotes.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.list =
                            false;

                        const records =
                            Array.isArray(
                                action.payload
                                    ?.records
                            )
                                ? action.payload.records
                                : [];

                        /*
                        |--------------------------------------------------------------------------
                        | Keep old + new list properties synchronized
                        |--------------------------------------------------------------------------
                        */

                        state.records =
                            records;

                        state.list =
                            records;

                        /*
                        |--------------------------------------------------------------------------
                        | Summary
                        |--------------------------------------------------------------------------
                        */

                        const calculatedSummary =
                            buildSummaryFromRecords(
                                records
                            );

                        state.listSummary = {
                            ...defaultListSummary,

                            ...calculatedSummary,

                            ...(
                                action.payload
                                    ?.summary ||
                                {}
                            ),
                        };

                        /*
                        |--------------------------------------------------------------------------
                        | Pagination
                        |--------------------------------------------------------------------------
                        */

                        const nextPagination = {
                            ...defaultPagination,

                            ...(
                                action.payload
                                    ?.pagination ||
                                {}
                            ),
                        };

                        state.pagination =
                            nextPagination;

                        state.listPagination =
                            nextPagination;
                    }
                )

                .addCase(
                    fetchCreditNotes.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.list =
                            false;

                        state.error.list =
                            action.payload ||
                            "Unable to fetch credit notes.";
                    }
                );

            /*
            |--------------------------------------------------------------------------
            | APPLY CREDIT NOTE
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    applyCreditNote.pending,
                    (
                        state
                    ) => {
                        state.loading.apply =
                            true;

                        state.error.apply =
                            "";

                        state.appliedResult =
                            null;
                    }
                )

                .addCase(
                    applyCreditNote.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.apply =
                            false;

                        state.error.apply =
                            "";

                        state.appliedResult =
                            action.payload;
                    }
                )

                .addCase(
                    applyCreditNote.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.apply =
                            false;

                        state.error.apply =
                            action.payload ||
                            "Unable to apply credit note.";
                    }
                );

            /*
            |--------------------------------------------------------------------------
            | CREDIT NOTE DETAILS
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchCreditNoteById.pending,
                    (
                        state
                    ) => {
                        state.loading.details =
                            true;

                        state.error.details =
                            "";
                    }
                )

                .addCase(
                    fetchCreditNoteById.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.details =
                            false;

                        state.selectedCreditNote =
                            action.payload;
                    }
                )

                .addCase(
                    fetchCreditNoteById.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.details =
                            false;

                        state.error.details =
                            action.payload ||
                            "Unable to fetch credit note.";
                    }
                );

            /*
            |--------------------------------------------------------------------------
            | CREDIT NOTES BY INVOICE
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchCreditNotesByInvoice.pending,
                    (
                        state
                    ) => {
                        state.loading.invoice =
                            true;

                        state.error.invoice =
                            "";
                    }
                )

                .addCase(
                    fetchCreditNotesByInvoice.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.invoice =
                            false;

                        state.invoiceCreditNotes =
                            action.payload
                                ?.records ||
                            [];

                        const calculatedSummary =
                            buildSummaryFromRecords(
                                state.invoiceCreditNotes
                            );

                        state.invoiceSummary = {
                            ...defaultInvoiceSummary,

                            ...calculatedSummary,

                            ...(
                                action.payload
                                    ?.summary ||
                                {}
                            ),
                        };
                    }
                )

                .addCase(
                    fetchCreditNotesByInvoice.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.invoice =
                            false;

                        state.error.invoice =
                            action.payload ||
                            "Unable to fetch invoice credit notes.";
                    }
                );

            /*
            |--------------------------------------------------------------------------
            | CONFIRM CREDIT NOTE
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    confirmCreditNote.pending,
                    (
                        state
                    ) => {
                        state.loading.confirm =
                            true;

                        state.error.confirm =
                            "";

                        state.successMessage =
                            "";
                    }
                )

                .addCase(
                    confirmCreditNote.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.confirm =
                            false;

                        const confirmedCreditNote =
                            action.payload;

                        state.selectedCreditNote =
                            confirmedCreditNote;

                        /*
                        |--------------------------------------------------------------------------
                        | Update every place where this Credit Note may exist
                        |--------------------------------------------------------------------------
                        */

                        state.records =
                            updateRecordById(
                                state.records,
                                confirmedCreditNote
                            );

                        state.list =
                            updateRecordById(
                                state.list,
                                confirmedCreditNote
                            );

                        state.invoiceCreditNotes =
                            updateRecordById(
                                state.invoiceCreditNotes,
                                confirmedCreditNote
                            );

                        /*
                        |--------------------------------------------------------------------------
                        | Recalculate summaries
                        |--------------------------------------------------------------------------
                        */

                        state.listSummary =
                            buildSummaryFromRecords(
                                state.list
                            );

                        state.invoiceSummary = {
                            ...state.invoiceSummary,

                            ...buildSummaryFromRecords(
                                state.invoiceCreditNotes
                            ),
                        };

                        state.successMessage =
                            "Credit note confirmed successfully.";
                    }
                )

                .addCase(
                    confirmCreditNote.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.confirm =
                            false;

                        state.error.confirm =
                            action.payload ||
                            "Unable to confirm credit note.";
                    }
                );

            /*
            |--------------------------------------------------------------------------
            | CANCEL CREDIT NOTE
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    cancelCreditNote.pending,
                    (
                        state
                    ) => {
                        state.loading.cancel =
                            true;

                        state.error.cancel =
                            "";

                        state.successMessage =
                            "";
                    }
                )

                .addCase(
                    cancelCreditNote.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.cancel =
                            false;

                        const cancelledCreditNote =
                            action.payload;

                        state.selectedCreditNote =
                            cancelledCreditNote;

                        /*
                        |--------------------------------------------------------------------------
                        | Update list + invoice list
                        |--------------------------------------------------------------------------
                        */

                        state.records =
                            updateRecordById(
                                state.records,
                                cancelledCreditNote
                            );

                        state.list =
                            updateRecordById(
                                state.list,
                                cancelledCreditNote
                            );

                        state.invoiceCreditNotes =
                            updateRecordById(
                                state.invoiceCreditNotes,
                                cancelledCreditNote
                            );

                        /*
                        |--------------------------------------------------------------------------
                        | Recalculate summaries
                        |--------------------------------------------------------------------------
                        */

                        state.listSummary =
                            buildSummaryFromRecords(
                                state.list
                            );

                        state.invoiceSummary = {
                            ...state.invoiceSummary,

                            ...buildSummaryFromRecords(
                                state.invoiceCreditNotes
                            ),
                        };

                        state.successMessage =
                            "Credit note cancelled successfully.";
                    }
                )

                .addCase(
                    cancelCreditNote.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.cancel =
                            false;

                        state.error.cancel =
                            action.payload ||
                            "Unable to cancel credit note.";
                    }
                );
        },
    });

/*
|--------------------------------------------------------------------------
| ACTIONS
|--------------------------------------------------------------------------
*/

export const {
    clearCreditNoteError,
    clearCreditNoteSuccess,
    clearSelectedCreditNote,
    clearInvoiceCreditNotes,
    clearCreditNoteList,
    clearAppliedCreditNote,
    resetCreditNoteState,
} = creditNoteSlice.actions;

/*
|--------------------------------------------------------------------------
| ROOT SELECTOR
|--------------------------------------------------------------------------
|
| IMPORTANT:
| Your existing reducer is registered as:
|
| state.creditNote
|
|--------------------------------------------------------------------------
*/

export const creditNoteStateSelector = (
    state
) => {
    return (
        state?.creditNote ||
        initialState
    );
};

/*
|--------------------------------------------------------------------------
| STANDALONE CREDIT NOTE LIST SELECTORS
|--------------------------------------------------------------------------
*/

export const creditNotesSelector = (
    state
) => {
    return (
        creditNoteStateSelector(
            state
        ).records ||
        []
    );
};

export const creditNoteListSelector = (
    state
) => {
    return (
        creditNoteStateSelector(
            state
        ).list ||
        []
    );
};

export const creditNoteListSummarySelector = (
    state
) => {
    return (
        creditNoteStateSelector(
            state
        ).listSummary ||
        defaultListSummary
    );
};

export const creditNotePaginationSelector = (
    state
) => {
    return (
        creditNoteStateSelector(
            state
        ).pagination ||
        defaultPagination
    );
};

export const creditNoteListPaginationSelector = (
    state
) => {
    return (
        creditNoteStateSelector(
            state
        ).listPagination ||
        defaultPagination
    );
};

/*
|--------------------------------------------------------------------------
| SELECTED CREDIT NOTE
|--------------------------------------------------------------------------
*/

export const selectedCreditNoteSelector = (
    state
) => {
    return (
        creditNoteStateSelector(
            state
        ).selectedCreditNote ||
        null
    );
};

/*
|--------------------------------------------------------------------------
| INVOICE CREDIT NOTE SELECTORS
|--------------------------------------------------------------------------
*/

export const invoiceCreditNotesSelector = (
    state
) => {
    return (
        creditNoteStateSelector(
            state
        ).invoiceCreditNotes ||
        []
    );
};

export const invoiceCreditNoteSummarySelector = (
    state
) => {
    return (
        creditNoteStateSelector(
            state
        ).invoiceSummary ||
        defaultInvoiceSummary
    );
};

/*
|--------------------------------------------------------------------------
| LOADING / ERROR / SUCCESS
|--------------------------------------------------------------------------
*/

export const creditNoteLoadingSelector = (
    state
) => {
    return (
        creditNoteStateSelector(
            state
        ).loading ||
        defaultLoading
    );
};

export const creditNoteErrorSelector = (
    state
) => {
    return (
        creditNoteStateSelector(
            state
        ).error ||
        defaultError
    );
};

export const creditNoteSuccessSelector = (
    state
) => {
    return (
        creditNoteStateSelector(
            state
        ).successMessage ||
        ""
    );
};

/*
|--------------------------------------------------------------------------
| APPLY CREDIT NOTE SELECTORS
|--------------------------------------------------------------------------
*/

export const creditNoteApplyLoadingSelector = (
    state
) => {
    return Boolean(
        creditNoteStateSelector(
            state
        ).loading
            ?.apply
    );
};

export const creditNoteApplyErrorSelector = (
    state
) => {
    return (
        creditNoteStateSelector(
            state
        ).error
            ?.apply ||
        ""
    );
};

export const appliedCreditNoteResultSelector = (
    state
) => {
    return (
        creditNoteStateSelector(
            state
        ).appliedResult ||
        null
    );
};

/*
|--------------------------------------------------------------------------
| REDUCER
|--------------------------------------------------------------------------
*/

export default creditNoteSlice.reducer;